import { useEffect, useState, useRef } from "react";
import { useHistoryManager } from "../history/useHistoryManager";

export const useModalManager = (
  storePath = "",
  showProgress = false,
  setShowProgress
) => {
  const [modal, setModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalState, setModalState] = useState("checkout");
  const [modalBackButton, setModalBackButton] = useState({
    show: false,
    onClick: null,
  });

  const scrollYRef = useRef(0);
  const onCloseCallback = useRef(null);

  const {
    revertModalHistory,
    markModalInHistory,
    setHistoryState,
    cleanNavigationPath,
  } = useHistoryManager(storePath);

  const openModal = (
    state,
    data = null,
    urlPath = "",
    prefix = true,
    onClose = null
  ) => {
    if (urlPath) {
      urlPath = cleanNavigationPath(urlPath, prefix);
      setHistoryState(urlPath, { modal: true });
    }
    setModalState(state);
    setModalData(data);
    if (typeof onClose === "function") {
      onCloseCallback.current = onClose;
    }
    setModal(true);
  };

  const resetModal = () => {
    setModal(false);
    setModalData(null);
    setModalState("");
    setModalBackButton({ show: false, onClick: null });
    onCloseCallback.current = null;
  };

  const closeModal = (trigger = "") => {
    if (!modal) return;
    if (onCloseCallback.current) {
      onCloseCallback.current(trigger);
    }
    if (showProgress === true) {
      setShowProgress(false);
    }
    resetModal();
    revertModalHistory(trigger);
  };

  // History state marking
  useEffect(() => {
    if (modal) {
      markModalInHistory();
    }
  }, [modal]);

  // ESC key handler
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && modal) {
        closeModal("Escape");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modal]);

  // Back button (popstate) handler
  useEffect(() => {
    if (typeof window === "undefined" || !modal) return;

    const handlePopState = () => closeModal("backButton");

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [modal]);

  // Scroll lock and body position when modal is open
  useEffect(() => {
    if (typeof window === "undefined" || !modal) return;

    scrollYRef.current = window.scrollY;
    const html = document.documentElement;
    const body = document.body;

    body.style.top = `-${scrollYRef.current}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.overscrollBehaviorY = "contain";

    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      body.style.overflowY = "";
      html.style.overscrollBehaviorY = "";
      html.style.overflow = "";
      body.style.overflow = "";

      window.scrollTo(0, scrollYRef.current);
    };
  }, [modal]);

  // Responsive --vh variable handling for mobile browsers
  useEffect(() => {
    if (typeof window === "undefined") return;

    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    let throttleTimeout = null;
    const throttledSetVH = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        setVH();
        throttleTimeout = null;
      }, 100);
    };

    setVH();

    window.addEventListener("resize", throttledSetVH);
    window.addEventListener("orientationchange", throttledSetVH);

    return () => {
      window.removeEventListener("resize", throttledSetVH);
      window.removeEventListener("orientationchange", throttledSetVH);
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, []);

  return {
    modal,
    modalData,
    modalState,
    modalBackButton,
    setModalBackButton,
    openModal,
    closeModal,
  };
};
